import { useEffect, useState } from "react";
import { createApiService } from "../shared/apiConfig";
import { FacilityApi } from "../shared/api/axios-client";

interface PayoutAccountStatusCache {
    isAccountOnboarded: boolean;
    cachedAt: number;
}

const payoutAccountStatusCacheTtlMs = 5 * 60 * 1000;

function payoutAccountStatusCacheKey( facilityId: number ) {
    return `facility:${facilityId}:payoutAccountStatus`;
}

function readCachedPayoutAccountStatus( facilityId: number ) {
    try {
        const raw = localStorage.getItem( payoutAccountStatusCacheKey( facilityId ) );
        if( !raw ) return undefined;
        const cached = JSON.parse( raw ) as PayoutAccountStatusCache;
        if( Date.now() - cached.cachedAt > payoutAccountStatusCacheTtlMs ) {
            return undefined;
        }
        return cached.isAccountOnboarded;
    }
    catch {
        return undefined;
    }
}

function writeCachedPayoutAccountStatus( facilityId: number, isAccountOnboarded: boolean ) {
    try {
        const cached: PayoutAccountStatusCache = {
            isAccountOnboarded,
            cachedAt: Date.now(),
        };
        localStorage.setItem( payoutAccountStatusCacheKey( facilityId ), JSON.stringify( cached ) );
    }
    catch {
    }
}

export function usePayoutAccountOnboardingStatus( facilityId: number ) {
    const [isPayoutAccountOnboarded, setIsPayoutAccountOnboarded] = useState<boolean | undefined>(
        () => readCachedPayoutAccountStatus( facilityId ) );

    useEffect( () => {
        let cancelled = false;
        const cachedStatus = readCachedPayoutAccountStatus( facilityId );
        setIsPayoutAccountOnboarded( cachedStatus );

        const facilityApi = createApiService( FacilityApi );
        facilityApi.getFacilityPayoutAccountAsync( { facilityId } )
            .then( res => {
                if( cancelled ) return;
                const isOnboarded = res.data.isAccountOnboarded ?? false;
                writeCachedPayoutAccountStatus( facilityId, isOnboarded );
                setIsPayoutAccountOnboarded( isOnboarded );
            } )
            .catch( () => {
                if( cancelled || cachedStatus !== undefined ) return;
                setIsPayoutAccountOnboarded( undefined );
            } );

        return () => {
            cancelled = true;
        };
    }, [facilityId] );

    return isPayoutAccountOnboarded;
}
